/**
 * Utility functions for processing cart API responses
 * Phase 3.2: Optimized for clean structure responses only - backend returns clean structure at root level
 * Legacy nested response handling removed as backend no longer uses dual structure
 */

import { CleanCartSummary } from "../types/cleanPricing";

interface CleanCartApiResponse extends CleanCartSummary {
  [key: string]: any;
}

/**
 * Processes clean structure cart API response for Redux dispatch
 * Optimized for Phase 3.2: Backend returns clean structure only at root level
 *
 * @param response - Clean cart data directly at root level
 * @returns Processed cart data with clean pricing at root level
 */
export const processCartResponse = (response: CleanCartApiResponse) => {
  console.log("🔄 [DEBUG] Processing clean structure cart response:", {
    cartId: response.cartId,
    cartVersion: response.cartVersion,
    groupCount: response.groups?.length || 0,
    hasAppliedCoupons: response.appliedCoupons?.length > 0,
    grandTotal: response.totals?.grandTotal,
    responseStructure: {
      hasCartId: !!response.cartId,
      hasGroups: !!response.groups,
      hasCleanSummary: !!response.cleanSummary,
      hasTotals: !!response.totals,
      responseKeys: Object.keys(response || {}),
    },
  });

  // FIXED: Debug partner data to ensure it's being preserved
  if (response.groups?.length > 0) {
    console.log(
      "🔄 [DEBUG] Group delivery fees and partner data in processCartResponse:",
      {
        groups: response.groups.map((group: any) => ({
          id: group.id,
          deliveryFee: group.deliveryFee,
          totalDeliveryFee: group.totalDeliveryFee,
          itemsWithPartnerData:
            group.items?.map((item: any) => ({
              cartCustomId: item.cartCustomId,
              productId: item.product?.id,
              hasPartner: !!item.product?.partner,
              partnerName: item.product?.partner?.name,
              hasDeliveryFeeZone: !!item.product?.partner?.deliveryFeeZone,
              hasDeliveryLeadTimeZone:
                !!item.product?.partner?.deliveryLeadTimeZone,
              hasDaysOpen: !!item.product?.partner?.daysOpen,
              deliveryFeeZone: item.product?.partner?.deliveryFeeZone
                ? {
                    id: item.product.partner.deliveryFeeZone.id,
                    name: item.product.partner.deliveryFeeZone.name,
                    documentId: item.product.partner.deliveryFeeZone.documentId,
                  }
                : null,
              daysOpenSample: item.product?.partner?.daysOpen
                ? {
                    monday: item.product.partner.daysOpen.monday,
                    mondayOpeningTime:
                      item.product.partner.daysOpen.mondayOpeningTime,
                  }
                : null,
            })) || [],
        })),
      }
    );
  }

  console.log(
    "🔄 [DEBUG] Cart data structure with partner validation:",
    JSON.stringify(response, null, 2)
  );

  return {
    cartId: response.cartId,
    cartVersion: response.cartVersion,
    cartStatus: "active", // Default status for active carts
    groups: response.groups || [],
    totals: response.totals,
    appliedCoupons: response.appliedCoupons || [],
    appliedPromotions: response.appliedPromotions || [],
    // Clean pricing data is the cart data itself
    cleanPricing: response,
    // Legacy fields for backward compatibility (computed from clean structure)
    totalPrice: response.totals?.grandTotal,
    totalDeliveryFee: response.totals?.deliveryFeeTotal,
    finalTotal: response.totals?.grandTotal,
    promotionalDiscountTotal: response.totals?.promotionalDiscountTotal || 0,
    couponDiscountTotal: response.totals?.couponDiscountTotal || 0,
  };
};
